import { LedgerError } from './errors';
import { assertAmount, formatMinor, type Currency } from './money';
import {
  assertBasisPoints,
  BPS_SCALE,
  multiplyByBasisPoints,
  type BasisPoints,
} from './rounding';

/** Section 5.2. Every listed title must offer at least this to creators. */
export const MINIMUM_CREATOR_RATE_BPS: BasisPoints = 500;

/**
 * What a creator earns on a sale they brought. Half the net, on every title.
 *
 * **This is the whole of the rate now (#129).** It used to be per title:
 * `product.creator_rate_bps` held a number a studio chose during submission,
 * `creator_rate_change` held the ones it had scheduled, and section 5.2's
 * fourteen-day notice existed because a studio could lower its own. That is
 * gone. The rate is the platform's, it is the same everywhere, and there is no
 * surface — form, endpoint, admin panel or migration — that can set a title to
 * anything else.
 *
 * **Why a constant rather than a column defaulted to 5000.** A column that
 * every writer sets to the same value is not a fixed rate, it is a rate that
 * *happens* to agree today, and the first thing to disagree with it is the one
 * nobody remembers to look at — a seed script, an import, a hand-written
 * `UPDATE` on a support ticket. A creator planning a video off the board and a
 * studio reading the number in its contract have to be reading the same fact,
 * so there is one place it is written down.
 *
 * **It is not read at clearing time from here.** Hard rule 4 still stands: an
 * order line carries the rate that was live when it cleared, copied onto the
 * row and never consulted from the catalogue again. `resolveLockedRates` reads
 * this constant to fill that copy in *today*; a line cleared under an earlier,
 * genuinely per-title rate keeps the number it cleared with, and every payout,
 * refund and dashboard figure recomputes from the line rather than from here.
 * Changing this value therefore changes what future sales earn, and changes
 * nothing about any sale that has already happened.
 *
 * Half, rather than a number chosen to look generous: somebody who bought
 * through a creator's code went out of their way to, and the creator spent
 * their own standing with an audience to send them.
 * `apps/app/src/components/creators-pitch.tsx` is where the rate is explained
 * to the audience it is about.
 */
export const CREATOR_RATE_BPS: BasisPoints = 5000;

/**
 * The platform's share of the distributable amount. All of it.
 *
 * **This was a record keyed by service tier and is now one number** (#144).
 * Section 5.3 set the platform fee at 15% on Self-Serve and 30% on Full
 * Service, and the remainder was the studio's — the whole point of the tier
 * was to say how a sale was divided between GOODBUILD and the company whose
 * game it was. GOODBUILD publishes nobody and the catalogue is its own, so
 * there is no second party to divide with and no tier to look up.
 *
 * `computeRevenueSplit` still takes the rate as an explicit locked input rather
 * than reading this, and that is hard rule 4 rather than ceremony: a line that
 * cleared in 2026 locked `platform_rate_bps = 3000` and must still reconcile
 * against what really happened. This constant is what a line clearing *today*
 * locks, which makes `developerNet` zero and `platformGross` the whole
 * distributable amount.
 */
export const PLATFORM_RATE_BPS: BasisPoints = 10_000;

/**
 * Section 5.4. Minimum transacted price, applied after discount rather than to
 * list price. Enforced at checkout, not here: this engine must still be able to
 * allocate an order that was placed under a different floor.
 */
export const MINIMUM_TRANSACTED_PRICE_MINOR: Readonly<Partial<Record<Currency, number>>> =
  Object.freeze({
    EUR: 299,
  });

/**
 * The rates an order line is bound to. Copied onto the order line when the
 * order clears and never read from the live catalogue again (hard rule 4): an
 * order pays out under the rates that were active when it cleared.
 */
export interface LockedRates {
  readonly creatorRateBps: BasisPoints;
  readonly platformRateBps: BasisPoints;
}

export interface RevenueSplitInput extends LockedRates {
  readonly currency: Currency;
  /** What the player was actually charged, after discount or promo code. */
  readonly pricePaid: number;
  /** VAT or equivalent, remitted by GOODBUILD as merchant of record. */
  readonly tax: number;
  /** Stripe cost at actual cost, allocated to this line. */
  readonly paymentFees: number;
}

export interface RevenueSplit extends RevenueSplitInput {
  readonly net: number;
  readonly commission: number;
  readonly distributable: number;
  /**
   * The share of the distributable amount that is not the platform's.
   *
   * **Zero on every line cleared since #144**, because `PLATFORM_RATE_BPS` is
   * the whole of it. It was the studio's share, and there are no studios. The
   * field stays rather than being deleted because a line that cleared under an
   * older rate still has one, and the conservation check below is what proves
   * a historical split still adds up.
   */
  readonly developerNet: number;
  readonly platformGross: number;
}

/**
 * Section 5.1, implemented in the stated order:
 *
 *   net            = price_paid - tax - payment_fees
 *   commission     = net * creator_rate
 *   distributable  = net - commission
 *   developer_net  = distributable * (1 - platform_rate)
 *   platform_gross = distributable * platform_rate
 *
 * Two rounding decisions, both deliberate and both load-bearing:
 *
 * 1. `distributable` is `net - commission`, not a second rounded product, so
 *    commission plus distributable is always exactly net.
 * 2. `developerNet` is the rounded product and `platformGross` takes the
 *    remainder. The sub-cent residual therefore always lands on GOODBUILD,
 *    never on the developer or the creator. Never invert this.
 *
 * Together these give: commission + developerNet + platformGross === net, and
 * net + tax + paymentFees === pricePaid, for every input. Both are asserted
 * before the value is returned.
 */
export function computeRevenueSplit(input: RevenueSplitInput): RevenueSplit {
  const { currency, pricePaid, tax, paymentFees, creatorRateBps, platformRateBps } = input;

  assertAmount(pricePaid, 'pricePaid');
  assertAmount(tax, 'tax');
  assertAmount(paymentFees, 'paymentFees');
  assertBasisPoints(creatorRateBps, 'creatorRateBps');
  assertBasisPoints(platformRateBps, 'platformRateBps');

  if (pricePaid < 0) throw new LedgerError(`pricePaid must not be negative, got ${pricePaid}`);
  if (tax < 0) throw new LedgerError(`tax must not be negative, got ${tax}`);
  if (paymentFees < 0)
    throw new LedgerError(`paymentFees must not be negative, got ${paymentFees}`);

  const net = pricePaid - tax - paymentFees;
  if (net < 0) {
    throw new LedgerError(
      `Net is negative: ${formatMinor(currency, pricePaid)} paid less ` +
        `${formatMinor(currency, tax)} tax less ${formatMinor(currency, paymentFees)} fees. ` +
        'A line that costs more to process than it earns must be rejected at checkout, ' +
        'which is what the section 5.4 price floor is for.',
    );
  }

  const commission = multiplyByBasisPoints(net, creatorRateBps);
  const distributable = net - commission;

  const developerRateBps = BPS_SCALE - platformRateBps;
  const developerNet = multiplyByBasisPoints(distributable, developerRateBps);
  const platformGross = distributable - developerNet;

  const split: RevenueSplit = Object.freeze({
    ...input,
    net,
    commission,
    distributable,
    developerNet,
    platformGross,
  });

  assertSplitConserves(split);
  return split;
}

/**
 * Conservation of money. Called on every split produced; a failure here means
 * the arithmetic above is wrong, not that the caller passed something odd.
 */
export function assertSplitConserves(split: RevenueSplit): void {
  const { currency, pricePaid, tax, paymentFees, net } = split;

  if (net + tax + paymentFees !== pricePaid) {
    throw new LedgerError(
      `Split does not conserve gross: net ${formatMinor(currency, net)} + tax ` +
        `${formatMinor(currency, tax)} + fees ${formatMinor(currency, paymentFees)} != ` +
        `price paid ${formatMinor(currency, pricePaid)}`,
    );
  }

  const distributed = split.commission + split.developerNet + split.platformGross;
  if (distributed !== net) {
    throw new LedgerError(
      `Split does not conserve net: commission ${formatMinor(currency, split.commission)} + ` +
        `developer ${formatMinor(currency, split.developerNet)} + platform ` +
        `${formatMinor(currency, split.platformGross)} = ${formatMinor(currency, distributed)}, ` +
        `expected ${formatMinor(currency, net)}`,
    );
  }

  if (split.commission + split.distributable !== net) {
    throw new LedgerError('Split does not conserve: commission + distributable != net');
  }
}

/**
 * Section 11. What a creator actually earns on one sale, which is the number
 * the opportunity board shows large. Computed rather than left as a percentage
 * so nobody has to compare 15% of 4.99 against 8% of 39.99 in their head.
 */
export function earningsPerSale(input: RevenueSplitInput): number {
  return computeRevenueSplit(input).commission;
}
